import cn from 'classnames';
import { useState } from 'react';

import { Skeleton } from '@/shared/ui';

import type { KbArticleRef, KbSectionNode } from '../lib';

import styles from './KnowledgeBase.module.scss';

type KbTreeSectionProps = {
  node: KbSectionNode;
  depth: number;
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
};

const KbTreeSection = (props: KbTreeSectionProps) => {
  const { node, depth, selectedSlug, onSelect } = props;
  const [expanded, setExpanded] = useState(depth === 0);

  return (
    <li className={styles.treeSection}>
      <button
        type="button"
        className={cn(styles.treeSectionToggle, {
          [styles.treeSectionExpanded]: expanded,
        })}
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        <span className={styles.treeSectionTitle}>{node.title}</span>
      </button>
      {expanded && (
        <>
          {node.sections.length > 0 && (
            <ul className={styles.treeSectionList}>
              {node.sections.map((child) => (
                <KbTreeSection
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  selectedSlug={selectedSlug}
                  onSelect={onSelect}
                />
              ))}
            </ul>
          )}
          {node.articles.length > 0 && (
            <ul className={styles.treeArticleList}>
              {node.articles.map((article) => (
                <li key={article.slug}>
                  <button
                    type="button"
                    className={cn(styles.treeArticleLink, {
                      [styles.treeArticleSelected]:
                        article.slug === selectedSlug,
                    })}
                    onClick={() => onSelect(article.slug)}
                  >
                    {article.title}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
};

type KbTreeProps = {
  sections: KbSectionNode[];
  // Section-less pages — rendered above the section folds, like the hub's
  // standalone sidebar.
  rootArticles: KbArticleRef[];
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
};

export const KbTree = (props: KbTreeProps) => {
  const { sections, rootArticles, selectedSlug, onSelect } = props;

  return (
    <ul className={styles.tree}>
      {rootArticles.map((article) => (
        <li key={article.slug}>
          <button
            type="button"
            className={cn(styles.treeArticleLink, {
              [styles.treeArticleSelected]: article.slug === selectedSlug,
            })}
            onClick={() => onSelect(article.slug)}
          >
            {article.title}
          </button>
        </li>
      ))}
      {sections.map((node) => (
        <KbTreeSection
          key={node.id}
          node={node}
          depth={0}
          selectedSlug={selectedSlug}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
};

// Tree-shaped skeleton shown while the structure loads: a section row, its
// nested page rows and further section rows at the tree's own metrics so
// the swap-in is calm.
const TREE_SKELETON_ROWS = [
  { width: '55%', indent: 0 },
  { width: '72%', indent: 1 },
  { width: '64%', indent: 1 },
  { width: '70%', indent: 1 },
  { width: '48%', indent: 0 },
  { width: '66%', indent: 1 },
  { width: '58%', indent: 1 },
  { width: '52%', indent: 0 },
  { width: '68%', indent: 1 },
  { width: '60%', indent: 0 },
];

export const KbTreeSkeleton = () => (
  <div className={styles.treeSkeleton} aria-hidden="true">
    {TREE_SKELETON_ROWS.map((row, index) => (
      <div
        key={index}
        className={styles.treeSkeletonRow}
        style={{ paddingInlineStart: row.indent * 22 }}
      >
        <Skeleton width={row.width} height={20} borderRadius={8} />
      </div>
    ))}
  </div>
);
